import { memo, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { IconCheckFill18 as CheckFilled } from "nucleo-ui-fill-18"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronRightOutline18 as ChevronRight,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { hasEnabledEmail } from "@productlane/lib/src/email-settings"
import { trialChecklistSteps } from "@productlane/lib/src/trial-checklist"
import { Dialog } from "@productlane/ui"
import {
	CONNECT_MCP_DESCRIPTION,
	CONNECT_MCP_TITLE,
	ConnectMcpContent,
} from "@productlane/ui/src/components/connect-mcp-content"
import { queries } from "@productlane/zero/queries"

import { api } from "@/lib/api"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useChecklistState } from "@/lib/states/checklist-state"
import { useTrial } from "@/lib/useTrial"
import { useZeroWorkspace } from "@/lib/zero"
import { computeChecklistVisibility } from "./checklist-visibility"

export function useChecklistItems() {
	const workspace = useZeroWorkspace()
	const { isTrial } = useTrial()
	const [widgetThreads] = useQuery(queries.widgetThreads())
	const [apiKeys] = useQuery(queries.apiKeysV2())
	const { data: connectedApps } = api.workspace.listConnectedApps.useQuery(
		undefined,
		{ enabled: isTrial },
	)
	const isLinearEnabled = useMemo(() => {
		return hasLinearConnection(workspace)
	}, [workspace])
	// The repo, not the install: linking one is what maps the product for the agent.
	const isGithubConnected = useMemo(() => {
		return !!workspace?.githubSettings?.repositoryFullName
	}, [workspace])
	const isMcpConnected = useMemo(() => {
		// OAuth (browser-auth) connection or a used v2 API key (bearer fallback).
		const hasOauthConnection = (connectedApps?.length ?? 0) > 0
		const hasUsedApiKey = (apiKeys ?? []).some(
			(key) => key.lastUsedAt != null && key.revokedAt == null,
		)
		return hasOauthConnection || hasUsedApiKey
	}, [connectedApps, apiKeys])
	const isEmailsEnabled = useMemo(() => {
		return !!hasEnabledEmail(workspace?.emailSettings)
	}, [workspace])
	const isWidgetEnabled = useMemo(() => {
		return (widgetThreads?.length ?? 0) > 0
	}, [widgetThreads])
	const steps = useMemo(
		() =>
			trialChecklistSteps({
				email: isEmailsEnabled,
				widget: isWidgetEnabled,
				github: isGithubConnected,
				linear: isLinearEnabled,
				mcp: isMcpConnected,
			}),
		[
			isEmailsEnabled,
			isWidgetEnabled,
			isGithubConnected,
			isLinearEnabled,
			isMcpConnected,
		],
	)
	const done = steps.filter((step) => step.isDone).length

	return {
		steps,
		progress: done / steps.length,
		total: steps.length,
		done,
	}
}

function useChecklistVisible() {
	const workspace = useZeroWorkspace()
	const { isTrial } = useTrial()
	const { progress } = useChecklistItems()
	return computeChecklistVisibility({
		hasWorkspace: !!workspace,
		isTrial,
		progress,
	})
}

export const ChecklistContent = memo(function ChecklistContent() {
	const [isMcpDialogOpen, setIsMcpDialogOpen] = useState(false)
	const { steps, done, total } = useChecklistItems()
	const { isCollapsed, toggleCollapsed } = useChecklistState()
	const checklistVisible = useChecklistVisible()
	if (!checklistVisible) {
		return null
	}

	const Chevron = isCollapsed ? ChevronRight : ChevronDown

	return (
		<div className="w-full px-2 pb-2">
			<div className="relative rounded-lg bg-white p-1.5 shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:shadow-black dark:ring-white/10">
				<button
					type="button"
					onClick={toggleCollapsed}
					aria-expanded={!isCollapsed}
					className="flex w-full select-none items-center gap-1.5 rounded px-1 py-1 text-left hover:bg-gray-150 dark:hover:bg-gray-800"
				>
					<span className="flex-1 truncate text-sm">Get started</span>
					<span className="flex-none text-sm tabular-nums text-tertiary">
						{done}/{total}
					</span>
					<Chevron className="h-3 w-3 flex-none text-tertiary" />
				</button>

				{!isCollapsed && (
					<div className="mt-1 space-y-0.5">
						{steps.map((step) =>
							step.id === "mcp" ? (
								<ChecklistItem
									key={step.id}
									text={step.label}
									isDone={step.isDone}
									onClick={() => {
										setIsMcpDialogOpen(true)
									}}
								/>
							) : (
								<ChecklistItem
									key={step.id}
									text={step.label}
									isDone={step.isDone}
									href={step.path}
								/>
							),
						)}
					</div>
				)}
			</div>
			<ConnectMcpDialog
				isOpen={isMcpDialogOpen}
				onOpenChange={setIsMcpDialogOpen}
			/>
		</div>
	)
})

const CHECKLIST_ROW_CLASSES =
	"-mx-0.5 flex w-[calc(100%+0.25rem)] select-none items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-gray-150 dark:hover:bg-gray-800"

interface ChecklistItemProps {
	href?: string
	onClick?: () => void
	text: string
	isDone: boolean
}

function ChecklistItem({ href, onClick, text, isDone }: ChecklistItemProps) {
	const inner = (
		<>
			<span
				aria-hidden="true"
				className={cn(
					"flex h-4 w-4 flex-none items-center justify-center rounded-full border",
					isDone
						? "border-green-500 bg-green-500 text-white"
						: "border-gray-300 dark:border-gray-600",
				)}
			>
				{isDone && <CheckFilled className="h-3 w-3" />}
			</span>
			<span className="sr-only">{isDone ? "Completed: " : "Incomplete: "}</span>
			<div
				className={cn(
					"flex-1 truncate text-sm",
					isDone ? "text-tertiary" : "text-primary",
				)}
			>
				{text}
			</div>
		</>
	)

	if (href) {
		return (
			<Link className={CHECKLIST_ROW_CLASSES} to={href}>
				{inner}
			</Link>
		)
	}

	return (
		<button type="button" className={CHECKLIST_ROW_CLASSES} onClick={onClick}>
			{inner}
		</button>
	)
}

function ConnectMcpDialog({
	isOpen,
	onOpenChange,
}: {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
}) {
	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={CONNECT_MCP_TITLE}
			description={CONNECT_MCP_DESCRIPTION}
		>
			<ConnectMcpContent />
		</Dialog>
	)
}
